import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export type FieldMapEntry =
  | { by: 'column'; value: string; createIfMissing?: boolean }
  | { by: 'constant'; value: string };
export type FieldMap = Record<string, FieldMapEntry>;

export type MappingTemplate = {
  id: string;
  name: string;
  sourceKind: 'csv' | 'rest' | 'webhook' | 'sftp';
  sourceConfig: unknown;
  targetKind: 'catalog_item' | 'pricing_entry';
  fieldMap: FieldMap;
  matchHeaders: string[];
};

export type UploadResult = {
  uploadId: string;
  rowCount: number;
  headers: string[];
  suggestedTemplateId: string | null;
};

export type RunResult = {
  runId: string;
  totals: { ok: number; errors: number; rows: number };
  errors: { rowIndex: number; sourceRow: Record<string, unknown>; error: string }[];
};

export function useUploadCsv() {
  return useMutation({
    mutationFn: async (file: File): Promise<UploadResult> => {
      const buffer = await file.arrayBuffer();
      const res = await apiClient.post<UploadResult>('/import-export/uploads', buffer, {
        headers: {
          'Content-Type': 'text/csv',
          'X-Original-Name': file.name,
        },
        transformRequest: [(data) => data],
      });
      return res.data;
    },
  });
}

export function useUploadPreview(uploadId: string | null) {
  return useQuery({
    queryKey: ['import-export', 'preview', uploadId],
    enabled: uploadId !== null && uploadId !== '',
    queryFn: async (): Promise<{ headers: string[]; rows: Record<string, unknown>[] }> => {
      const res = await apiClient.get(`/import-export/uploads/${uploadId}/preview`);
      return res.data as { headers: string[]; rows: Record<string, unknown>[] };
    },
  });
}

export function useMappingTemplates(
  filter: {
    sourceKind?: string;
    targetKind?: string;
  } = {},
) {
  return useQuery({
    queryKey: ['import-export', 'templates', filter],
    queryFn: async (): Promise<MappingTemplate[]> => {
      const res = await apiClient.get<MappingTemplate[]>('/import-export/mapping-templates', {
        params: filter,
      });
      return res.data;
    },
  });
}

export function useCreateMappingTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      sourceKind: 'csv';
      sourceConfig: Record<string, unknown>;
      targetKind: 'catalog_item' | 'pricing_entry';
      fieldMap: FieldMap;
      matchHeaders: string[];
    }): Promise<MappingTemplate> => {
      const res = await apiClient.post<MappingTemplate>('/import-export/mapping-templates', input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['import-export', 'templates'] }),
  });
}

export function useDryRun() {
  return useMutation({
    mutationFn: async (input: {
      uploadId: string;
      fieldMap: FieldMap;
      targetKind: 'catalog_item';
    }): Promise<RunResult> => {
      const res = await apiClient.post<RunResult>('/import-export/imports:dry-run', input);
      return res.data;
    },
  });
}

/** RL-825: a live import is queued, not run in the request; follow it by this id. */
export type LiveRunStarted = { runId: string };

export function useLiveRun() {
  return useMutation({
    mutationFn: async (input: {
      uploadId: string;
      fieldMap: FieldMap;
      targetKind: 'catalog_item';
    }): Promise<LiveRunStarted> => {
      const res = await apiClient.post<LiveRunStarted>('/import-export/imports:live', input);
      return res.data;
    },
  });
}

export type ImportRunStatus =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'succeeded_with_errors'
  | 'failed';

/** A queued or finished import run, as the CSV import page shows it. */
export type ImportRunView = {
  status: ImportRunStatus;
  totals: RunResult['totals'];
  errors: RunResult['errors'];
};

/** `GET /import-export/runs/:id`: the run row with its first dead letters. */
type ImportRunDetail = {
  status: ImportRunStatus;
  totalsJson: { ok?: number; errors?: number; rows?: number } | null;
  deadLetters: { rowIndex: number; sourceRowJson: unknown; errorJson: unknown }[];
};

const FINISHED: ReadonlySet<ImportRunStatus> = new Set([
  'succeeded',
  'succeeded_with_errors',
  'failed',
]);

export function isImportRunFinished(status: ImportRunStatus | undefined): boolean {
  return status !== undefined && FINISHED.has(status);
}

/** Polling interval while a live run is queued or running. */
export const IMPORT_RUN_POLL_MS = 1_500;

/**
 * RL-825: follow a live import run until it finishes. Polls while the run is
 * queued or running and stops once it has succeeded or failed; asks nothing
 * while there is no run.
 */
export function useImportRun(runId: string | null) {
  return useQuery({
    queryKey: ['import-export', 'run', runId],
    enabled: runId !== null,
    queryFn: async (): Promise<ImportRunView> => {
      const res = await apiClient.get<ImportRunDetail>(`/import-export/runs/${runId}`);
      return toRunView(res.data);
    },
    // A failed lookup stops the polling: a missing run (404) would otherwise be
    // asked for every interval, forever (PC review of 4b5bc624). The page shows
    // the status as unavailable and checks the same run again on request; a
    // failed lookup says nothing about whether the import itself failed.
    refetchInterval: (query) =>
      query.state.status === 'error' || isImportRunFinished(query.state.data?.status)
        ? false
        : IMPORT_RUN_POLL_MS,
  });
}

function toRunView(run: ImportRunDetail): ImportRunView {
  const totals = run.totalsJson ?? {};
  return {
    status: run.status,
    totals: { ok: totals.ok ?? 0, errors: totals.errors ?? 0, rows: totals.rows ?? 0 },
    errors: run.deadLetters.map((letter) => ({
      rowIndex: letter.rowIndex,
      sourceRow: (letter.sourceRowJson ?? {}) as Record<string, unknown>,
      error: deadLetterMessage(letter.errorJson),
    })),
  };
}

function deadLetterMessage(error: unknown): string {
  if (error !== null && typeof error === 'object') {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string') return message;
  }
  return JSON.stringify(error);
}
